import { t } from '@exam-prep/i18n';
import type { MiniPlan } from './plans-response';
import { dayIst } from '../../../lib/format';

/** What a plan gives, in one short line. */
export function meta(p: MiniPlan): string {
  const g = p.grants[0];
  const parts: string[] = [];
  if (g?.type === 'mock_credit') parts.push(t('miniapp.plans.mocks', { n: g.qty ?? 1 }));
  else if (g?.type === 'doubt_credit') parts.push(t('miniapp.plans.doubts', { n: g.qty ?? 1 }));
  else parts.push(t('miniapp.plans.pro'));
  if (g?.type === 'pro' && p.doubtCredits !== null)
    parts.push(t('miniapp.plans.doubts', { n: p.doubtCredits }));
  if (p.subscription) parts.push(t('miniapp.plans.monthly'));
  else if (p.validityDays) parts.push(t('miniapp.plans.days', { n: p.validityDays }));
  else parts.push(t('miniapp.plans.till_exam'));
  return parts.join(' · ');
}

const isPack = (p: MiniPlan): boolean => p.kind === 'credit_pack';

export const untilExam = (p: MiniPlan): boolean => !p.subscription && !p.validityDays;

export const months = (p: MiniPlan): number | null => {
  const d = p.maxDays ?? p.validityDays;
  return d ? Math.max(1, Math.round(d / 30.4375)) : null;
};

export const credits = (p: MiniPlan): number | null =>
  p.doubtCredits ?? p.grants.find((g) => g.type === 'doubt_credit')?.qty ?? null;

/** The Best value plan of a group: the pack's `highlight`, else the cheapest per month / per credit. */
export function bestKey(group: readonly MiniPlan[]): string | null {
  const marked = group.find((p) => p.highlight === 'best_value');
  if (marked) return marked.key;
  if (group.length < 2) return null;
  let best: { key: string; v: number } | null = null;
  for (const p of group) {
    const n = isPack(p) ? credits(p) : untilExam(p) ? null : months(p);
    if (!n) continue;
    const v = p.stars / n;
    if (best === null || v < best.v) best = { key: p.key, v };
  }
  return best?.key ?? null;
}

/** Plans (Best value first, then the one-exam plan, then the rest) and Doubt Packs (smallest first). */
export function groupPlans(plans: readonly MiniPlan[]): { pro: MiniPlan[]; packs: MiniPlan[] } {
  const proAll = plans.filter((p) => !isPack(p) && p.kind !== 'mock_pack');
  const best = bestKey(proAll);
  const rank = (p: MiniPlan): number => (p.key === best ? 0 : untilExam(p) ? 1 : 2);
  return {
    pro: proAll
      .map((p, i) => ({ p, i }))
      .sort((a, b) => rank(a.p) - rank(b.p) || a.i - b.i)
      .map((x) => x.p),
    packs: plans.filter(isPack).sort((a, b) => (credits(a) ?? 0) - (credits(b) ?? 0)),
  };
}

/** "65 ⭐/month · save 51% vs Monthly", "from 75 ⭐/month", or null for Monthly itself. */
export function valueLine(p: MiniPlan, monthly: MiniPlan | null): string | null {
  const m = months(p);
  if (!m || m === 1 || isPack(p)) return null;
  const pm = p.stars / m;
  const n = String(Math.round(pm));
  if (untilExam(p)) return t('miniapp.plans.from_per_month', { n });
  const save = monthly ? Math.floor((1 - pm / monthly.stars) * 100) : 0;
  const line = t('miniapp.plans.per_month', { n });
  return save > 0 ? `${line} · ${t('miniapp.plans.save_vs_monthly', { n: save })}` : line;
}

/** "0.67 ⭐ per credit · 23% less per credit" for a Doubt Pack. */
export function packLine(p: MiniPlan, packs: readonly MiniPlan[]): string | null {
  const n = credits(p);
  if (!n) return null;
  const each = p.stars / n;
  const parts = [t('miniapp.plans.per_credit', { n: each.toFixed(2) })];
  const first = packs[0];
  const base = first ? first.stars / (credits(first) ?? 1) : null;
  if (base && first !== p) {
    const pct = Math.floor((1 - each / base) * 100);
    if (pct > 0) parts.push(t('miniapp.plans.less_per_credit', { n: pct }));
  }
  return parts.join(' · ');
}

/** Short lines on what a plan would add for me (ADR 0039); empty when there is nothing to say. */
export function coverageLines(p: MiniPlan): string[] {
  const c = p.coverage;
  if (!c) return [];
  if (c.status === 'included')
    return [
      t('miniapp.plans.included', { name: c.byName, date: dayIst(c.until) }),
      t('miniapp.plans.covered_next'),
    ];
  if (c.status === 'subscribed')
    return [
      t('miniapp.plans.subscribed', { date: dayIst(c.until) }),
      t('miniapp.plans.covered_next'),
    ];
  if (c.status === 'extends')
    return [t('miniapp.plans.adds_days', { days: c.days, date: dayIst(c.after) })];
  return c.leftover.map(
    (l) =>
      t('miniapp.plans.switch_note', { exam: l.examName, date: dayIst(l.until) }) +
      (l.subscription ? ` ${t('miniapp.plans.cancel_hint')}` : ''),
  );
}

export const isCovered = (p: MiniPlan): boolean =>
  p.coverage?.status === 'included' || p.coverage?.status === 'subscribed';
